// crewbit-admin/src/hooks/useCreatePublicBooking.ts
//
// Schedule Module Phase B.1 — paired hook for `create_public_booking` RPC
// (id-variant). Wraps the admin walk-in submit path; mirrors mobile's
// authenticated customer-portal lift. The id-variant predates Phase A —
// dual-writes bookings + schedule_entries + pending_notifications correctly
// per Probe §6 (preserved through A10) but does NOT yet accept p_idempotency_key.
// Spec: docs/references/schedule/SCHEDULE_MODULE_WIRING_v1.md §4 + §11.2.
//
// Pre-Phase-A shape note: the RPC currently emits string errors on failure
// ('Service not found', 'No availability at this time', 'Time slot no longer
// available'). The wrapper's strict envelope guard maps non-{code,...} error
// payloads to UNKNOWN; user copy falls through defaultErrorMessages.UNKNOWN.
// Future hardening session (queued post-launch) will lift the RPC to
// {success, error: {code, message}} shape. For B1, accept this as-is.

import { useCallback } from 'react';
import { callScheduleRpc, type ScheduleRpcResult } from '../lib/scheduleRpc';

export type PublicBookingSource = 'app' | 'phone' | 'walk_in';

export interface CreatePublicBookingInput {
  businessId: string;
  serviceId: string;
  /** YYYY-MM-DD. */
  date: string;
  /** HH:MM (24h). */
  startTime: string;
  clientName: string;
  clientPhone: string;
  clientEmail?: string;
  /** Optional — RPC autopicks first-available operator at this time if absent. */
  operatorId?: string;
  source: PublicBookingSource;
  /**
   * REQUIRED — UUID v4 from form-load (caller convention). The id-variant of
   * create_public_booking does NOT accept p_idempotency_key today; the hook
   * accepts the field for forward-compat with the planned RPC hardening but
   * does NOT pass it through to the RPC in B1.
   */
  idempotencyKey: string;
}

export type CreatePublicBookingResult = ScheduleRpcResult<{
  success: true;
  booking_id: string;
  service_name: string;
  start_time: string;
  end_time: string;
  status: string;
}>;

export function useCreatePublicBooking() {
  return useCallback(
    async (input: CreatePublicBookingInput): Promise<CreatePublicBookingResult> => {
      // p_idempotency_key intentionally omitted — RPC doesn't accept it
      // pre-hardening. All other args mirror the live RPC signature exactly.
      return callScheduleRpc<{
        success: true;
        booking_id: string;
        service_name: string;
        start_time: string;
        end_time: string;
        status: string;
      }>('create_public_booking', {
        p_business_id: input.businessId,
        p_service_id: input.serviceId,
        p_date: input.date,
        p_start_time: input.startTime,
        p_client_name: input.clientName,
        p_client_phone: input.clientPhone,
        p_client_email: input.clientEmail ?? null,
        p_operator_id: input.operatorId ?? null,
        p_source: input.source,
      });
    },
    [],
  );
}
